(() => {
  const deployment = Object.freeze({ origin: "https://dtfsheet-sample-concierge-preview.vercel.app", browserPath: "/api/free-sample/concierge/browser", previewPath: "/sample", contractVersion: "free-sample-v5", released: false, commerceOrigin: null })
  const key = `sample-concierge-funnel-owner-v2:${deployment.origin}`
  const uuid = value => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  const revision = value => Number.isSafeInteger(value) && value >= 0
  const create = ({ enabled, origin = deployment.origin, funnelVersion, form, button, phoneInput, disclosureElement, readPhone, onChange = () => {} }) => {
    const permitted = enabled === true && deployment.released === true && origin === deployment.origin
    let record = null
    let available = false
    let cancellationAvailable = false
    let cancellationOnly = false
    let recoveryRequired = false
    let cancellationRequested = false
    let captureWork = null
    let adapter = null
    let lastState = "disabled"
    let consentAt = ""
    let withdrawalWork = null
    const info = () => ({ available, cancellationAvailable, cancellationOnly, paused: !permitted, reason: !permitted ? "protected_preview_paused" : null, state: record?.withdrawalPending ? "cancelling" : record?.nativeRejoinRequired ? "needs-keyword" : record?.cancelled ? "cancelled" : lastState, recoveryRequired, attemptId: record?.attemptId || null, revision: record?.revision || 0, cancelled: record?.cancelled === true, cancellationPending: record?.withdrawalPending === true, consentAt })
    const notify = () => onChange(info())
    const persist = () => {
      try { sessionStorage.setItem(key, JSON.stringify(record)); return true } catch { return false }
    }
    const api = async command => {
      if (!permitted || !record) throw new Error("protected_preview_paused")
      if (cancellationOnly && !["status", "withdraw"].includes(command.action)) throw new Error("cancellation_only")
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 8000)
      try {
        const response = await fetch(`${deployment.origin}${deployment.browserPath}`, {
          method: "POST", mode: "cors", credentials: "omit", redirect: "error", cache: "no-store", referrerPolicy: "no-referrer",
          headers: { "Content-Type": "application/json", "X-Concierge-Owner": record.ownerProof },
          body: JSON.stringify(command), signal: controller.signal
        })
        const body = await response.json()
        if (!response.ok || !body || Array.isArray(body) || body.ok !== true || !revision(body.revision) || !["empty", "pending", "revoked"].includes(body.state) || body.enrolled !== false || body.maySubmit !== (command.action === "claim" && body.status === "claimed") || body.identityBound !== false || body.sampleEligible !== false || body.executable !== false || (command.action !== "bootstrap" && body.sessionId !== command.sessionId)) throw new Error("owned_request_unconfirmed")
        return body
      } finally { clearTimeout(timer) }
    }
    const status = async (allowExpired = false) => {
      const sessionId = record.sessionId
      const minimumRevision = record.revision
      const body = await api({ action: "status", sessionId })
      if (body.status !== "current" || typeof body.ownershipExpired !== "boolean" || record.sessionId !== sessionId || body.sessionId !== sessionId || !revision(body.revision) || body.revision < Math.max(minimumRevision, record.revision) || body.enrolled !== false || body.maySubmit !== false || (body.attemptId !== null && !uuid(body.attemptId))) throw new Error("invalid_owned_status")
      cancellationAvailable = true
      if (body.ownershipExpired) { cancellationOnly = true; available = false }
      record.nativeRejoinRequired = body.nativeSubscription === "native_rejoin_required" || (record.attemptId === body.attemptId && record.nativeRejoinRequired === true)
      record.revision = body.revision
      record.attemptId = body.attemptId
      record.attemptState = body.state
      if (body.state === "revoked") { record.cancelled = true; record.withdrawalPending = false; lastState = "cancelled" }
      persist()
      notify()
      if (cancellationOnly && !allowExpired) throw new Error("ownership_expired")
      return body
    }
    const performWithdrawal = async () => {
      cancellationRequested = true
      record.cancelled = true
      record.withdrawalPending = true
      lastState = "cancelling"
      persist()
      notify()
      if (captureWork) { try { await captureWork } catch {} }
      let current
      try { current = await status(true) } catch { return { ok: false, cancelled: false } }
      if (!current.attemptId && !recoveryRequired) { record.withdrawalPending = false; lastState = "cancelled"; persist(); notify(); return { ok: true, cancelled: true } }
      if (!current.attemptId) return { ok: false, cancelled: false }
      if (current.state === "revoked") { record.withdrawalPending = false; lastState = "cancelled"; persist(); notify(); return { ok: true, cancelled: true } }
      const command = record.withdraw && record.withdraw.attemptId === current.attemptId && record.withdraw.expectedRevision === current.revision
        ? record.withdraw
        : { action: "withdraw", sessionId: record.sessionId, attemptId: current.attemptId, requestId: crypto.randomUUID(), expectedRevision: current.revision }
      record.withdraw = command
      persist()
      try {
        const body = await api(command)
        if (body.status !== "withdrawn" || body.attemptId !== command.attemptId || body.revision !== command.expectedRevision + 1 || body.consentActive !== false || body.enrolled !== false || body.maySubmit !== false || body.providerRecall !== false) return { ok: false, cancelled: false }
        record.revision = body.revision
        record.attemptState = "revoked"
        record.withdraw = null
        record.withdrawalPending = false
        lastState = "cancelled"
        recoveryRequired = false
        persist()
        notify()
        return { ok: true, cancelled: true }
      } catch { return { ok: false, cancelled: false } }
    }
    const withdrawn = () => {
      if (!withdrawalWork) withdrawalWork = performWithdrawal().finally(() => { withdrawalWork = null })
      return withdrawalWork
    }
    const mapped = (action, attemptId, claimId, decision, expiry = Date.now() + 1000) => ({ ...action, attemptId, claimId, decision, expiresAt: Math.min(expiry, action.startedAt + 10000) })
    const bridge = {
      claim: async input => {
        const action = Object.freeze({ sessionId: input.sessionId, actionId: input.actionId, phone: input.phone, disclosureVersion: input.disclosureVersion, companyId: input.companyId, listId: input.listId, startedAt: input.startedAt, signal: input.signal })
        const sessionId = record.sessionId
        if (action.sessionId !== sessionId) throw new Error("session_mismatch")
        if (!available || recoveryRequired || cancellationRequested) throw new Error("fresh_owned_action_required")
        const expectedRevision = record.revision
        record.captureRequestId = crypto.randomUUID()
        record.claimRequestId = null
        record.consentAt = new Date().toISOString()
        record.pending = "capture"
        consentAt = record.consentAt
        if (!persist()) throw new Error("ownership_storage_unavailable")
        const captureRequestId = record.captureRequestId
        const capturedAt = consentAt
        const command = Object.freeze({ action: "capture", sessionId, requestId: captureRequestId, expectedRevision, phone: action.phone, affirmative: true, consentVersion: "sample-concierge-v1-2026-08-29", consentAt: capturedAt })
        recoveryRequired = true
        captureWork = api(command)
        const captured = await captureWork
        captureWork = null
        if (captured.status !== "saved" || captured.state !== "pending" || captured.consentStored !== true || captured.enrolled !== false || captured.nativeSubscription !== "not_verified" || captured.maySubmit !== false || !uuid(captured.attemptId) || captured.revision !== expectedRevision + 1) throw new Error("capture_not_verified")
        const attemptId = captured.attemptId
        const capturedRevision = captured.revision
        record.attemptId = attemptId
        record.nativeRejoinRequired = false
        record.revision = capturedRevision
        record.attemptState = "pending"
        record.pending = "reconcile"
        persist()
        if (cancellationRequested || action.signal?.aborted) throw new Error("cancelled")
        const native = await api({ action: "reconcile", sessionId, attemptId })
        if (cancellationRequested || action.signal?.aborted) throw new Error("cancelled")
        if (acceptNative(native, sessionId, attemptId, capturedRevision)) return mapped(action, attemptId, captureRequestId, "needs_keyword")
        if (native.status === "native_confirmed" && native.state === "pending" && native.nativeConfirmed === true) {
          record.pending = "native_confirmed"
          persist()
          return { ...mapped(action, attemptId, captureRequestId, "already_subscribed"), nativeProof: { phone: action.phone, accountId: "VmfH8w", consent: "SUBSCRIBED", canReceiveMarketing: true, observedAt: Date.now() } }
        }
        if (native.status !== "needs_opt_in" || native.nativeConfirmed !== false) throw new Error("native_not_resolved")
        const expected = capturedRevision
        const claimRequestId = crypto.randomUUID()
        record.claimRequestId = claimRequestId
        record.pending = "claim"
        persist()
        const claim = await api({ action: "claim", sessionId, attemptId, requestId: claimRequestId, expectedRevision: expected, phone: action.phone })
        const expires = Date.parse(claim.expiresAt)
        if (cancellationRequested || action.signal?.aborted) throw new Error("cancelled")
        if (claim.status !== "claimed" || claim.state !== "pending" || claim.maySubmit !== true || claim.enrolled !== false || record.sessionId !== sessionId || record.attemptId !== attemptId || record.revision !== expected || claim.sessionId !== sessionId || claim.phone !== action.phone || claim.attemptId !== attemptId || claim.revision !== expected + 1 || claim.provider?.companyId !== "VmfH8w" || claim.provider?.listId !== "XKCTan" || claim.provider?.revision !== "2026-07-15" || !Number.isFinite(expires) || expires <= Date.now() || expires > Date.now() + 5000) throw new Error("claim_not_usable")
        record.revision = claim.revision
        record.pending = "claimed"
        persist()
        return mapped(action, attemptId, claimRequestId, "submit_once", expires)
      },
      record: async ({ outcome, sessionId, attemptId }) => {
        if (sessionId !== record.sessionId || attemptId !== record.attemptId) return { ok: false }
        const normalized = outcome === "accepted_pending_confirmation" ? "accepted" : ["rejected", "throttled"].includes(outcome) ? "rejected" : "unknown"
        record.pending = normalized
        persist()
        const body = await api({ action: "report", sessionId, attemptId, outcome: normalized })
        return { ok: body.status === "reported" && body.enrolled === false && body.maySubmit === false && body.telemetryTrusted === false && body.attemptId === attemptId && body.revision === record.revision }
      },
      cancel: withdrawn,
      reconcile: async () => {
        const current = await status()
        if (cancellationRequested || record.cancelled || current.state === "revoked") return { cancelled: true }
        if (!current.attemptId) return { confirmed: false }
        const body = await api({ action: "reconcile", sessionId: record.sessionId, attemptId: current.attemptId })
        if (acceptNative(body, record.sessionId, current.attemptId, current.revision)) return { confirmed: false, cancelled: true }
        return { confirmed: body.status === "native_confirmed" && body.state === "pending" && body.nativeConfirmed === true && body.enrolled === false && body.maySubmit === false && body.attemptId === current.attemptId && body.revision === current.revision, phoneBound: true, accountId: "VmfH8w", attemptId: current.attemptId }
      }
    }
    const acceptNative = (body, sessionId, attemptId, expectedRevision) => {
      if (record.sessionId !== sessionId || record.attemptId !== attemptId || record.revision !== expectedRevision || body.attemptId !== attemptId || body.sessionId !== sessionId || body.enrolled !== false || body.maySubmit !== false) throw new Error("native_binding_mismatch")
      if (body.status === "native_rejoin_required" && body.nativeConfirmed === false && revision(body.revision) && body.revision >= expectedRevision && body.revision <= expectedRevision + 1 && (body.revision === expectedRevision || body.state === "revoked")) {
        record.revision = body.revision
        record.nativeRejoinRequired = true
        record.attemptState = body.state
        persist()
        notify()
        return true
      }
      if (body.revision !== expectedRevision) throw new Error("native_revision_mismatch")
      return false
    }
    const makeAdapter = () => window.DtfsSampleSmsClient.create({ enabled: available, captureEnabled: available, form, button, phoneInput, disclosureElement, readPhone, bridge })
    const prepare = async (existingSessionId, hasExistingState) => {
      if (!permitted || !window.DtfsSampleSmsClient) return { sessionId: existingSessionId, available: false }
      try {
        const value = JSON.parse(sessionStorage.getItem(key) || "null")
        if (value && /^[A-Za-z0-9_-]{43}$/.test(value.ownerProof) && uuid(value.bootstrapId) && revision(value.revision) && (value.sessionId === null || uuid(value.sessionId)) && (value.attemptId === null || uuid(value.attemptId))) record = value
      } catch {}
      if (hasExistingState && (!uuid(existingSessionId) || !record || record.sessionId !== existingSessionId)) return { sessionId: existingSessionId, available: false, legacy: true }
      if (!hasExistingState && record?.sessionId) return { sessionId: existingSessionId, available: false, legacy: true }
      if (!record) {
        const bytes = crypto.getRandomValues(new Uint8Array(32))
        record = { ownerProof: btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""), bootstrapId: crypto.randomUUID(), revision: 0, sessionId: null, attemptId: null, cancelled: false }
        if (!persist()) return { sessionId: existingSessionId, available: false }
      }
      try {
        if (existingSessionId) {
          await status(true)
          recoveryRequired = Boolean(record.pending || record.attemptId)
          cancellationRequested = record.cancelled === true
          consentAt = record.consentAt || ""
          if (record.cancelled || record.withdrawalPending) await withdrawn()
          if (cancellationOnly) {
            lastState = "expired"
            notify()
            return { sessionId: record.sessionId, available: false, cancellationAvailable: true, cancellationOnly: true, recovery: recoveryRequired }
          }
        } else {
          const body = await api({ action: "bootstrap", bootstrapId: record.bootstrapId, funnelVersion: deployment.contractVersion })
          if (body.status !== "owned" || body.attemptId !== null || !uuid(body.sessionId) || body.revision !== 0 || !Number.isFinite(Date.parse(body.expiresAt)) || Date.parse(body.expiresAt) <= Date.now()) throw new Error("bootstrap_not_verified")
          record.sessionId = body.sessionId
          record.expiresAt = body.expiresAt
          record.revision = body.revision
          persist()
        }
        cancellationAvailable = true
        available = true
        adapter = makeAdapter()
        lastState = record.nativeRejoinRequired ? "needs-keyword" : recoveryRequired ? "recovery" : "idle"
        return { sessionId: record.sessionId, available: true, recovery: recoveryRequired }
      } catch { return { sessionId: existingSessionId, available: false, unavailable: true } }
    }
    const submit = async event => {
      if (!available) return { state: "disabled" }
      if (recoveryRequired) {
        try {
          if (record.cancelled) await withdrawn()
          else await status()
        } catch {}
        return { state: "recovery", consentStored: Boolean(record.attemptId), cancelled: record.cancelled === true }
      }
      const result = await adapter.submit(event, { sessionId: record.sessionId })
      lastState = result.state
      return { ...result, consentStored: Boolean(record.attemptId), consentAt }
    }
    const withdraw = async () => {
      if (!permitted || !cancellationAvailable) return { ok: false, cancelled: false, paused: !permitted }
      if (adapter) await adapter.cancel()
      return withdrawn()
    }
    const replace = async () => {
      if (!available) return false
      const done = await withdraw()
      if (!done.cancelled || !available || cancellationOnly) return false
      cancellationRequested = false
      recoveryRequired = false
      record.cancelled = false
      record.withdrawalPending = false
      record.pending = null
      persist()
      adapter = makeAdapter()
      lastState = "idle"
      notify()
      return true
    }
    const recover = async () => {
      if (!permitted || !cancellationAvailable) return null
      adapter?.rearm()
      const current = await status(true)
      if (record.cancelled || record.withdrawalPending) await withdrawn()
      return current
    }
    return Object.freeze({ prepare, submit, withdraw, replace, checkout: () => adapter?.handoff(), info, recover })
  }
  window.DtfsSampleConcierge = Object.freeze({ create, deployment })
})()
